import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import FilterSearch from "../../components/Filtering/FilterSearch";
import SearchButton from "../../components/buttons/SearchButton";
import FilterRightBox from "../../components/Filtering/FilterRightBoxOffering";
import BreadCrumb from "../../components/HeaderComponents/BreadCrum";
import DataTable from "../../components/DataTable/DataTable";
import { useWorkerApiHelper } from "../../hooks/useWorkerApiHelper";
import Swal from "sweetalert2";
import { showErrorAlert } from "../../utils/showErrorAlert";
import { useTableControls } from "../../hooks/useTableControls";
import { Popover } from "../../components/DataTable/Popover";
import { useFilteredData } from "../../hooks/useFilteredData";
import { useAuthRoles } from "../../hooks/useAuthRoles";
import CustomButton from "../../components/buttons/CustomButton";
import ParticipantModal from "../../components/ParticipantModal/ParticipantModal";
import AddIcon from "@mui/icons-material/Add";
import Tabs from "../../components/DataTable/Tabs";
import StatusChip from "../../components/StatusChip/StatusChip";
import {
  buildParticipantApprovalAcceptHtml,
  getParticipantApprovalAcceptPayload,
  buildParticipantApprovalRejectHtml,
  buildParticipantTechnicalDataHtml,
  buildConnectorTechnicalDataBody,
  getParticipantTechnicalDataPayload,
  buildParticipantTechnicalDataPayloads,
  filterParticipantsByManagementTab,
  getParticipantApprovalRejectPayload,
  getParticipantConnectorRoles,
  getParticipantId,
  getParticipantLifecycleStatus,
  getParticipantManagementActionKey,
  mountParticipantApprovalUserAutocomplete,
  parseWorkerPayload,
  parseSingleParticipant,
  PARTICIPANT_TECHNICAL_DATA_ADD_CONNECTOR_CHECKBOX_ID,
  unmountParticipantApprovalUserAutocomplete,
} from "../../hooks/participants/helpers";
import { useGetParticipants } from "../../hooks/participants/useGetParticipants";
import { usePostParticipantApproval } from "../../hooks/participants/usePostParticipantApproval";
import { usePostParticipantConnectorData } from "../../hooks/participants/usePostParticipantConnectorData";
import { usePostParticipantActivate } from "../../hooks/participants/usePostParticipantActivate";
import { usePostParticipantRevertRevoke } from "../../hooks/participants/usePostParticipantRevertRevoke";
import { usePostParticipantRevertWithdraw } from "../../hooks/participants/usePostParticipantRevertWithdraw";
import { usePostParticipantWithdraw } from "../../hooks/participants/usePostParticipantWithdraw";
import {
  getParticipantDisplayName,
  getParticipantStatusLabel,
  getParticipantTechnicalStatusLabel,
} from "../../utils/participants";
import {
  getAdditionalParticipantActions,
  getMatchedApprovalUsers,
  getOldestUpdatedParticipantId,
  getParticipantActionLabel,
  loadEnabledInternalUsers,
  loadUsersWithRealmRoles,
  shouldHideOldestParticipantActions,
} from "./ParticipantManagement.helpers";
import "./ParticipantApprovalDialog.css";

function ParticipantManagement() {
  const { callApiWithWorker } = useWorkerApiHelper();
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState("ACTIVE");
  const [openModal, setOpenModal] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const navigate = useNavigate();
  const {
    tableData,
    searchTerm,
    pendingSearchTerm,
    filters,
    open,
    pagination,
    rowCount,
    setTableData,
    setPagination,
    setRowCount,
    handleSearch,
    handleClearSearch,
    handleSearchSubmit,
    handleSearchKeyDown,
    handleClose,
    handleApplyFilters,
  } = useTableControls([]);
  const { data: pageResponse } = useGetParticipants({
    callApiWithWorker,
    lang: i18n.language,
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
    status: undefined,
  });

  const { mutateAsync: updateParticipantApproval } = usePostParticipantApproval(
    {
      callApiWithWorker,
      t,
      onErrorAlert: showErrorAlert,
    },
  );
  const { mutateAsync: revertParticipantWithdraw } =
    usePostParticipantRevertWithdraw({
      callApiWithWorker,
      t,
    });
  const { mutateAsync: revertParticipantRevoke } =
    usePostParticipantRevertRevoke({
      callApiWithWorker,
      t,
    });
  const { mutateAsync: withdrawParticipant } = usePostParticipantWithdraw({
    callApiWithWorker,
    t,
  });
  const { mutateAsync: createParticipantConnectorData } =
    usePostParticipantConnectorData({
      callApiWithWorker,
      t,
      onErrorAlert: showErrorAlert,
    });
  const { mutateAsync: activateParticipant } = usePostParticipantActivate({
    callApiWithWorker,
    t,
    onErrorAlert: showErrorAlert,
  });

  useEffect(() => {
    const content = filterParticipantsByManagementTab(
      pageResponse?.content,
      activeTab,
    );
    setTableData(content);
    setRowCount(content.length);
  }, [activeTab, pageResponse, setTableData, setRowCount]);

  const { isProvider, token } = useAuthRoles();

  const handleEdit = (data) => {
    navigate(`/editparticipant/${getParticipantId(data)}`, {
      state: { record: data, from: "participantmanagement" },
    });
  };

  const handleOpenModal = (row) => {
    setSelectedParticipant(getParticipantId(row));
    setOpenModal(true);
  };

  const loadApprovalUsers = async (participantId) => {
    let enabledUsers = [];

    try {
      enabledUsers = await loadEnabledInternalUsers(token);
    } catch (error) {
      console.error("Error loading internal Keycloak users:", error);
      await Swal.fire({
        title: t("participant.approval.acceptUserLoadErrorTitle", "Error"),
        text: t(
          "participant.approval.acceptUserLoadErrorMessage",
          "Could not load the enabled Keycloak users.",
        ),
        icon: "error",
        confirmButtonText: t("common.accept", "Accept"),
      });
      return null;
    }

    const matchedUsers = getMatchedApprovalUsers(enabledUsers, participantId);

    if (!matchedUsers.length) {
      await Swal.fire({
        title: t(
          "participant.approval.noAcceptUsersTitle",
          "No users available",
        ),
        text: t(
          "participant.approval.noAcceptUsersMessage",
          "There are no enabled Keycloak users available for this approval.",
        ),
        icon: "warning",
        confirmButtonText: t("common.accept", "Accept"),
      });
      return null;
    }

    return loadUsersWithRealmRoles(matchedUsers, token, (user, error) => {
      console.error(
        `Error fetching roles for Keycloak user ${user.username}:`,
        error,
      );
    });
  };

  const loadParticipantForApproval = async (participantId) => {
    try {
      const response = await callApiWithWorker(
        `/registry/participants/${encodeURIComponent(participantId)}` +
          `?lang=${encodeURIComponent(i18n.language)}`,
        {
          method: "GET",
          connector: "springConnector",
        },
      );

      return parseSingleParticipant(response);
    } catch (error) {
      console.error("Error loading participant for approval:", error);
      await Swal.fire({
        title: t("participant.approval.participantLoadErrorTitle", "Error"),
        text: t(
          "participant.approval.participantLoadErrorMessage",
          "Could not load the participant before approval.",
        ),
        icon: "error",
        confirmButtonText: t("common.accept", "Accept"),
      });
      return null;
    }
  };

  const handleAcceptLikeFlow = async (
    participantId,
    {
      action = "accept",
      titleKey = "participant.approval.acceptUserTitle",
      titleFallback = "Approve participant",
      promptKey = "participant.approval.acceptUserPrompt",
      confirmKey = "participant.approval.accept",
      confirmFallback = "Accept",
      commentDefaultKey = "participant.approval.acceptCommentDefault",
      commentDefaultFallback = "Approved by admin after review",
    } = {},
  ) => {
    const participant = await loadParticipantForApproval(participantId);
    if (!participant) return;

    const participantRoles = getParticipantConnectorRoles(participant);
    const isDualRoleParticipant = ["data_provider", "data_consumer"].every(
      (role) => participantRoles.includes(role),
    );

    const enabledUsers = await loadApprovalUsers(participantId);
    if (!enabledUsers) return;

    const usersById = enabledUsers.reduce((acc, user) => {
      acc[user.id] = user;
      return acc;
    }, {});

    const defaultComment = t(commentDefaultKey, commentDefaultFallback);
    const acceptResult = await Swal.fire({
      title: t(titleKey, titleFallback),
      html: buildParticipantApprovalAcceptHtml(
        t,
        enabledUsers,
        defaultComment,
        promptKey,
      ),
      icon: "info",
      iconColor: "var(--main-color)",
      showCancelButton: true,
      confirmButtonText: t(confirmKey, confirmFallback),
      cancelButtonText: t("common.cancel"),
      buttonsStyling: false,
      focusConfirm: false,
      customClass: {
        popup: "participant-approval-dialog",
        confirmButton: "participant-approval-confirm",
        cancelButton: "participant-approval-cancel",
        actions: "participant-approval-actions",
      },
      didOpen: (popup) => {
        mountParticipantApprovalUserAutocomplete(popup, t, enabledUsers);
      },
      willClose: (popup) => {
        unmountParticipantApprovalUserAutocomplete(popup);
      },
      preConfirm: () => {
        const payload = getParticipantApprovalAcceptPayload(
          Swal.getPopup(),
          usersById,
        );

        if (!payload.selectedUserId) {
          Swal.showValidationMessage(
            t(
              "participant.approval.userRequired",
              "Select a Keycloak user before continuing.",
            ),
          );
          return false;
        }

        if (!payload.keycloak?.username) {
          Swal.showValidationMessage(
            t(
              "participant.approval.userInvalid",
              "The selected Keycloak user is invalid.",
            ),
          );
          return false;
        }

        if (!payload.keycloak.roles?.length) {
          Swal.showValidationMessage(
            t(
              "participant.approval.userRolesRequired",
              "The selected Keycloak user has no assignable roles.",
            ),
          );
          return false;
        }

        if (
          isDualRoleParticipant &&
          !payload.keycloak.roles.some((role) =>
            ["data_provider", "data_consumer"].includes(role),
          )
        ) {
          Swal.showValidationMessage(
            t(
              "participant.approval.userRoleInvalid",
              "The selected Keycloak user does not have a supported participant role.",
            ),
          );
          return false;
        }

        return {
          comment: payload.comment || defaultComment,
          keycloak: payload.keycloak,
        };
      },
    });

    if (!acceptResult.isConfirmed) return;

    await updateParticipantApproval({
      participantId,
      action,
      payload: acceptResult.value,
    });

    if (!isDualRoleParticipant) return;

    const selectedUserRole = acceptResult.value?.keycloak?.roles?.find((role) =>
      ["data_provider", "data_consumer"].includes(role),
    );
    const oppositeRole =
      selectedUserRole === "data_provider" ? "data_consumer" : "data_provider";
    const username = String(
      acceptResult.value?.keycloak?.username ?? "",
    ).trim();
    const participantName =
      getParticipantDisplayName(participant, i18n.language) ||
      String(
        participant?.name ?? participant?.legal_name ?? participantId,
      ).trim();

    try {
      const separatedClientResponse = await callApiWithWorker(
        "/keycloak/internal/clients/separated",
        {
          method: "POST",
          connector: "connectorMaster",
          body: {
            username: `${username}_${oppositeRole}`,
            organization: participantName,
            participantId,
            roles: [oppositeRole],
          },
        },
      );

      const status =
        separatedClientResponse?.statusCode ??
        separatedClientResponse?.status ??
        separatedClientResponse?.data?.statusCode;
      if (status && status >= 400) {
        throw new Error(`Separated client creation failed (${status})`);
      }
    } catch (error) {
      console.error("Error creating separated participant client:", error);
      await Swal.fire({
        title: t("participant.approval.separatedClientErrorTitle", "Error"),
        text: t(
          "participant.approval.separatedClientErrorMessage",
          "The participant was approved, but the additional role client could not be created.",
        ),
        icon: "error",
        confirmButtonText: t("common.accept", "Accept"),
      });
    }
  };

  const handleReactivationDecision = async (row) => {
    const participantId = getParticipantId(row);
    const participantStatus = getParticipantLifecycleStatus(row);
    const result = await Swal.fire({
      title: t(
        "participant.approval.reactivate.title",
        "Reactivate participant",
      ),
      text: t(
        "participant.approval.reactivate.prompt",
        "Are you sure you want to reactivate this participant?",
      ),
      icon: "question",
      iconColor: "var(--main-color)",
      showCancelButton: true,
      confirmButtonText: t("participant.approval.reactivate.confirm"),
      cancelButtonText: t("common.cancel"),
      buttonsStyling: false,
      customClass: {
        popup: "participant-approval-dialog",
        confirmButton: "participant-approval-confirm",
        cancelButton: "participant-approval-cancel",
        actions: "participant-approval-actions",
      },
    });

    if (!result.isConfirmed) return;

    if (participantStatus === "revoked") {
      await revertParticipantRevoke({ participantId });
      return;
    }

    await revertParticipantWithdraw({
      participantId,
      successMessageKey: "participant.approval.updated.reactivate",
      successMessageFallback: "Participant reactivated successfully",
    });
  };

  const handleDeactivateDecision = async (row) => {
    const participantId = getParticipantId(row);
    const result = await Swal.fire({
      title: t("participant.approval.deactivate.title"),
      text: t("participant.approval.deactivate.prompt"),
      icon: "warning",
      iconColor: "var(--main-color)",
      showCancelButton: true,
      confirmButtonText: t("participant.approval.deactivate.confirm"),
      cancelButtonText: t("common.cancel"),
      buttonsStyling: false,
      customClass: {
        popup: "participant-approval-dialog",
        confirmButton: "participant-approval-deny",
        cancelButton: "participant-approval-cancel",
        actions: "participant-approval-actions",
      },
    });

    if (!result.isConfirmed) return;

    await withdrawParticipant({
      participantId,
      successMessageKey: "participant.approval.updated.deactivate",
      successMessageFallback: "Participant deactivated successfully",
    });
  };

  const buildConnectorRequestPayload = (participant) => ({
    requestId: crypto.randomUUID(),
    requestStatus: "FINISHED",
    requestDate: new Date().toISOString().replace(/Z$/, ""),
    requestorId:
      participant?.externalIdentifier ??
      participant?.identifier?.[0]?.notation ??
      "",
    requestorContactPoint: participant?.contactPoint?.email ?? "",
  });

  const createConnectorRequestForParticipant = async (participantId, row) => {
    const response = await callApiWithWorker(
      `/registry/connector-requests/participants/${encodeURIComponent(
        participantId,
      )}`,
      {
        method: "POST",
        connector: "springConnector",
        body: buildConnectorRequestPayload(row),
      },
    );
    const connectorRequest = parseWorkerPayload(response);
    const connectorRequestId =
      connectorRequest?.data?.id ?? connectorRequest?.id ?? "";

    if (!connectorRequestId) {
      throw new Error("Connector request response did not include data.id");
    }

    return connectorRequestId;
  };

  const createConnectorDataPlaceholder = async (participantId, payload) => {
    return callApiWithWorker(
      `/registry/connector-data/participants/${encodeURIComponent(
        participantId,
      )}`,
      {
        method: "POST",
        connector: "springConnector",
        body: payload,
      },
    );
  };

  const getAdditionalConnectorRole = (row, currentPayload) => {
    const currentRole = String(currentPayload?.ssoo ?? "").trim();
    const participantRoles = getParticipantConnectorRoles(row);
    const explicitAdditionalRole = participantRoles.find(
      (role) => role !== currentRole,
    );

    if (explicitAdditionalRole) return explicitAdditionalRole;
    if (currentRole === "data_provider") return "data_consumer";
    if (currentRole === "data_consumer") return "data_provider";

    return participantRoles[0] ?? "data_consumer";
  };

  const openTechnicalDataDialog = async (row) => {
    const participantId = getParticipantId(row);

    let connectorDataResponse;
    try {
      connectorDataResponse = await callApiWithWorker(
        `/registry/connector-data/${encodeURIComponent(participantId)}`,
        {
          method: "GET",
          connector: "springConnector",
        },
      );
    } catch (error) {
      console.error("Error loading connector data:", error);
      await Swal.fire({
        title: t("participant.technicalDataDialog.loadErrorTitle", "Error"),
        text: t(
          "participant.technicalDataDialog.loadErrorMessage",
          "Could not load the technical data for this participant.",
        ),
        icon: "error",
        confirmButtonText: t("common.accept", "Accept"),
      });
      return;
    }

    const connectorData = parseWorkerPayload(connectorDataResponse);
    const isActiveParticipant = getParticipantLifecycleStatus(row) === "active";
    const activateAfterSave = !isActiveParticipant;

    const basePayloads = buildParticipantTechnicalDataPayloads({
      participant: row,
      connectorData,
      participantName: getParticipantDisplayName(row, i18n.language),
    });
    if (!basePayloads.length) {
      await Swal.fire({
        title: t("participant.technicalDataDialog.roleErrorTitle", "Error"),
        text: t(
          "participant.technicalDataDialog.roleErrorMessage",
          "This participant does not have a supported technical role.",
        ),
        icon: "error",
        confirmButtonText: t("common.accept", "Accept"),
      });
      return;
    }

    let payload = basePayloads.length === 1 ? basePayloads[0] : basePayloads;
    const connectorDataEntries = Array.isArray(connectorData)
      ? connectorData
      : [];
    let canAddConnectorForm =
      connectorDataEntries.length === 1 && basePayloads.length === 1;
    let connectorDataPostFallback = null;

    const renderTechnicalDataHtml = (options = {}) =>
      buildParticipantTechnicalDataHtml(t, payload, {
        canAddConnector: canAddConnectorForm,
        ...options,
      });

    const addConnectorForm = async () => {
      const connectorRequestId = await createConnectorRequestForParticipant(
        participantId,
        row,
      );
      const participantName = getParticipantDisplayName(row, i18n.language);
      const nextConnectorData = [
        connectorDataEntries[0],
        {
          connectorRequest: { id: connectorRequestId },
          participant: {
            dsbParticipantId: participantId,
            roles: [getAdditionalConnectorRole(row, basePayloads[0])],
          },
          participantName,
        },
      ];
      const nextPayloads = buildParticipantTechnicalDataPayloads({
        participant: row,
        connectorData: nextConnectorData,
        participantName,
      });

      if (nextPayloads.length < 2) {
        throw new Error("Could not build the second connector form");
      }

      payload = nextPayloads;
      connectorDataPostFallback = {
        connectorRequest: { id: connectorRequestId },
        participant: { dsbParticipantId: participantId },
        participantName,
      };
      canAddConnectorForm = false;
    };

    const result = await Swal.fire({
      title: t("participant.technicalDataDialog.title", "Technical data"),
      html: renderTechnicalDataHtml(),
      icon: "info",
      iconColor: "var(--main-color)",
      showCancelButton: true,
      confirmButtonText: t(
        "participant.technicalDataDialog.confirm",
        "Send technical data",
      ),
      cancelButtonText: t("common.cancel"),
      buttonsStyling: false,
      focusConfirm: false,
      customClass: {
        popup: "participant-approval-dialog",
        confirmButton: "participant-approval-confirm",
        cancelButton: "participant-approval-cancel",
        actions: "participant-approval-actions",
      },
      didOpen: (popup) => {
        const checkbox = popup?.querySelector(
          `#${PARTICIPANT_TECHNICAL_DATA_ADD_CONNECTOR_CHECKBOX_ID}`,
        );

        checkbox?.addEventListener("change", async (event) => {
          if (!event.target.checked) return;

          event.target.disabled = true;
          Swal.update?.({
            html: renderTechnicalDataHtml({
              addConnectorChecked: true,
              addConnectorLoading: true,
            }),
          });

          try {
            await addConnectorForm();
            Swal.resetValidationMessage?.();
            Swal.update?.({
              html: renderTechnicalDataHtml(),
            });
          } catch (error) {
            console.error("Error creating connector request:", error);
            canAddConnectorForm = true;
            Swal.update?.({
              html: renderTechnicalDataHtml(),
            });
            Swal.showValidationMessage?.(
              t(
                "participant.technicalDataDialog.addConnectorError",
                "Could not create the connector request for the second form.",
              ),
            );
          }
        });
      },
      preConfirm: () => {
        const result = getParticipantTechnicalDataPayload(
          Swal.getPopup(),
          payload,
        );

        if (!result.payload) {
          const fieldLabel = t(
            `participant.technicalDataDialog.fields.${result.invalidFieldKey}`,
            result.invalidFieldKey,
          );

          Swal.showValidationMessage(
            t(
              "participant.technicalDataDialog.validation.invalidField",
              `Review the value for ${fieldLabel}.`,
              {
                field: fieldLabel,
              },
            ),
          );
          return false;
        }

        return result.payload;
      },
    });

    if (!result.isConfirmed) return;

    try {
      const submittedRawPayload = result.value ?? payload;
      const submittedPayload = Array.isArray(submittedRawPayload)
        ? submittedRawPayload.map((item) =>
            buildConnectorTechnicalDataBody(item),
          )
        : buildConnectorTechnicalDataBody(submittedRawPayload);

      if (connectorDataPostFallback) {
        const submittedPayloads = Array.isArray(submittedPayload)
          ? submittedPayload
          : [submittedPayload];
        const addedConnectorPayload =
          submittedPayloads[1] ??
          submittedPayloads[submittedPayloads.length - 1];

        await createConnectorDataPlaceholder(
          participantId,
          buildConnectorTechnicalDataBody(
            addedConnectorPayload,
            connectorDataPostFallback,
          ),
        );
      }

      await createParticipantConnectorData({
        participantId,
        payload: submittedPayload,
        method: "PUT",
        activateAfterSave,
      });
    } catch (_error) {}
  };

  const handleTechnicalDataDecision = async (row) => {
    await openTechnicalDataDialog(row);
  };

  const handleActivateDecision = async (row) => {
    await activateParticipant({
      participantId: getParticipantId(row),
    });
  };

  const handleApprovalDecision = async (row) => {
    const actionKey = getParticipantManagementActionKey(row);
    if (actionKey === "reactivate") {
      await handleReactivationDecision(row);
      return;
    }
    if (actionKey === "activate") {
      await handleActivateDecision(row);
      return;
    }
    if (actionKey === "technicalData") {
      await handleTechnicalDataDecision(row);
      return;
    }
    if (actionKey === "deactivate") {
      await handleDeactivateDecision(row);
      return;
    }

    const participantId = getParticipantId(row);
    const result = await Swal.fire({
      title: t("participant.approval.title"),
      text: t("participant.approval.prompt"),
      icon: "question",
      iconColor: "var(--main-color)",
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: t("participant.approval.accept"),
      denyButtonText: t("participant.approval.reject"),
      cancelButtonText: t("common.cancel"),
      buttonsStyling: false,
      customClass: {
        popup: "participant-approval-dialog",
        confirmButton: "participant-approval-confirm",
        denyButton: "participant-approval-deny",
        cancelButton: "participant-approval-cancel",
        actions: "participant-approval-actions",
      },
    });
    if (!result.isConfirmed && !result.isDenied) return;

    if (result.isDenied) {
      const rejectionResult = await Swal.fire({
        title: t("participant.approval.rejectReasonTitle"),
        html: buildParticipantApprovalRejectHtml(t),
        icon: "warning",
        iconColor: "var(--main-color)",
        showCancelButton: true,
        confirmButtonText: t("participant.approval.reject"),
        cancelButtonText: t("common.cancel"),
        buttonsStyling: false,
        focusConfirm: false,
        customClass: {
          popup: "participant-approval-dialog",
          confirmButton: "participant-approval-deny",
          cancelButton: "participant-approval-cancel",
          actions: "participant-approval-actions",
        },
        preConfirm: () => {
          const { reason, comment } = getParticipantApprovalRejectPayload(
            Swal.getPopup(),
          );

          if (!reason) {
            Swal.showValidationMessage(
              t("participant.approval.reasonRequired"),
            );
            return false;
          }

          return { reason, comment };
        },
      });

      if (!rejectionResult.isConfirmed) return;

      await updateParticipantApproval({
        participantId,
        action: "reject",
        payload: rejectionResult.value,
      });
      return;
    }

    await handleAcceptLikeFlow(participantId);
  };

  const resolveParticipantName = (row) =>
    getParticipantDisplayName(row, i18n.language);

  const columns = [
    {
      header: t("participant.name"),
      accessorFn: (row) => resolveParticipantName(row),
      meta: { width: "25%" },
    },
    {
      header: t("wizard.steps.dataset.identifier"),
      accessorFn: (row) => row.identifier?.[0]?.notation ?? "",
      meta: { width: "15%" },
    },
    {
      header: t("participant.status"),
      accessorFn: (row) => getParticipantStatusLabel(row.status, t),
      cell: ({ getValue, row }) => (
        <StatusChip label={getValue()} value={row.original?.status} />
      ),
      meta: { width: "20%" },
    },
    {
      header: t("participant.technicalData"),
      accessorFn: (row) =>
        getParticipantTechnicalStatusLabel(row.technicalFormStatus, t),
      cell: ({ getValue, row }) => (
        <StatusChip
          label={getValue() || "—"}
          value={row.original?.technicalFormStatus}
        />
      ),
      meta: { width: "15%" },
    },
    {
      header: t("participant.registered"),
      accessorKey: "updated_at",
      meta: { width: "25%" },
      cell: ({ getValue }) => {
        const date = new Date(getValue());
        return date.toLocaleString();
      },
    },
  ];
  const filteredData = useFilteredData(tableData, searchTerm, filters, columns);

  const oldestActiveParticipantId = useMemo(
    () =>
      activeTab === "ACTIVE" ? getOldestUpdatedParticipantId(filteredData) : null,
    [activeTab, filteredData],
  );

  const shouldHideOldestActiveParticipantActions = (row) => {
    return (
      activeTab === "ACTIVE" &&
      shouldHideOldestParticipantActions(row, oldestActiveParticipantId)
    );
  };

  const tableTabs = {
    ACTIVE: { label: t("participant.activeParticipants") },
    INACTIVE: { label: t("participant.revokedParticipants") },
  };

  return (
    <section className="pb4">
      <div className="w100 flexbread">
        <div className="divbreadcrum w90">
          <BreadCrumb />
        </div>
      </div>
      <div className="container containerflex">
        <div key={t.language} className="w90 labeltittle">
          <h2 className="tittlecatalog">
            {t("participant.participantManagement")}
          </h2>
          {isProvider && (
            <CustomButton
              onClick={() => navigate("/createparticipant")}
              startIcon={<AddIcon />}
            >
              {t("participant.addParticipant")}
            </CustomButton>
          )}
        </div>
        <div key={t.language} className="w86 filteractions pt3">
          <FilterSearch
            value={pendingSearchTerm}
            onChange={handleSearch}
            onClear={handleClearSearch}
            onKeyDown={handleSearchKeyDown}
          />
          <div className="plr2">
            <SearchButton onClick={handleSearchSubmit} />
          </div>
        </div>
        <FilterRightBox
          open={open}
          onClose={handleClose}
          onApplyFilters={handleApplyFilters}
        />
        <Tabs
          tabs={tableTabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
        <div className="w90 bgwhite mtb2">
          <div className="plr3 ptb">
            <DataTable
              data={filteredData}
              columns={columns}
              onEdit={(row) => handleEdit(row)}
              onRevoke={handleApprovalDecision}
              getRevokeLabel={(row) => getParticipantActionLabel(row, t)}
              getAdditionalActions={(row) =>
                getAdditionalParticipantActions(row, t, openTechnicalDataDialog)
              }
              shouldHideActions={shouldHideOldestActiveParticipantActions}
              onRowClick={handleOpenModal}
              showActions={true}
              initialSorting={[{ id: "updated_at", desc: true }]}
              pagination={pagination}
              setPagination={setPagination}
              rowCount={rowCount}
            />
          </div>
        </div>
      </div>
      {openModal && selectedParticipant ? (
        <Popover open={openModal} onClose={() => setOpenModal(false)}>
          <ParticipantModal
            participantId={selectedParticipant}
            onClose={() => setOpenModal(false)}
          />
        </Popover>
      ) : null}
      <div></div>
    </section>
  );
}

export default ParticipantManagement;
